import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Clock, Receipt, ShoppingCart, Coins, Percent, TrendingUp, Settings2, Download, Plus, Trash2, EyeOff } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { api, fmtIDR, wibDateISO } from "../lib/api";
import { describeActivity } from "../lib/activity";
import { useAuth } from "../context/AuthContext";
import { Panel } from "../lib/ui";
import { PrayerWidget } from "../components/PrayerWidget";
import { PendingShipmentsCard } from "../components/PendingShipmentsCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

const CH_COLORS = ["#8A5A44", "#3B82F6", "#F59E0B", "#10B981", "#8B5CF6", "#EC4899", "#64748B", "#EF4444"];
const iso = (d) => wibDateISO(d);
const SIZE_SPAN = { S: "lg:col-span-2", M: "lg:col-span-3", L: "lg:col-span-6" };

const KPI_META = {
  keep_value: { label: "Total Keep", icon: Bookmark, sub: "keep_units", subLabel: "Item keep ditahan", money: true, color: "text-amber-600" },
  preorder_value: { label: "Total Pre-Order", icon: Clock, sub: "preorder_units", subLabel: "Item pre-order", money: true, color: "text-sky-600" },
  total_sales: { label: "Total Sales", icon: Receipt, sub: "total_units", subLabel: "Item terjual", money: true, color: "text-cocoa" },
  total_transactions: { label: "Total Transaksi", icon: ShoppingCart, subLabel: "Transaksi", money: false, color: "text-blue-600" },
  cost: { label: "HPP / Cost", icon: Coins, subLabel: "Modal barang", money: true, color: "text-rose-500" },
  discount: { label: "Diskon", icon: Percent, subLabel: "Total potongan", money: true, color: "text-violet-500" },
  profit: { label: "Profit", icon: TrendingUp, subLabel: "Laba kotor", money: true, color: "text-emerald-600" },
};

function range(preset) {
  const now = new Date();
  const t = iso(now);
  if (preset === "today") return [t, t];
  if (preset === "yesterday") { const y = new Date(now); y.setDate(y.getDate() - 1); return [iso(y), iso(y)]; }
  if (preset === "week") { const s = new Date(now); const dow = (s.getDay() + 6) % 7; s.setDate(s.getDate() - dow); return [iso(s), t]; }
  if (preset === "month") { const s = new Date(now.getFullYear(), now.getMonth(), 1); return [iso(s), t]; }
  return [t, t];
}

const greeting = () => {
  const h = new Date().getHours();
  if (h < 11) return "Selamat Pagi";
  if (h < 15) return "Selamat Siang";
  if (h < 19) return "Selamat Sore";
  return "Selamat Malam";
};

export default function Dashboard() {
  const { user, can } = useAuth();
  const init = range("today");
  const [start, setStart] = useState(init[0]);
  const [end, setEnd] = useState(init[1]);
  const [preset, setPreset] = useState("today");
  const [channel, setChannel] = useState("all");
  const [group, setGroup] = useState("all");
  const [data, setData] = useState(null);
  const [layout, setLayout] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cfgOpen, setCfgOpen] = useState(false);

  useEffect(() => { api.get("/dashboards/me/layout").then((r) => setLayout(r.data.items || [])).catch(() => setLayout([])); }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/dashboard/sales", { params: { start, end, channel, group } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [start, end, channel, group]);
  useEffect(() => { load(); }, [load]);

  const applyPreset = (p) => { const [s, e] = range(p); setPreset(p); setStart(s); setEnd(e); };
  const onDate = (setter) => (v) => { setter(v); setPreset(""); };

  const cards = data?.cards || {};
  const masks = data?.card_masks || {};
  const percents = data?.card_percents || {};
  const channels = data?.channels || [];

  const exportCsv = () => {
    if (!data) return;
    const rows = [["Top Products"], ["No", "Nama", "Warna", "Ukuran", "Items"]];
    (data.top_products || []).forEach((p, i) => rows.push([i + 1, p.name, p.color, p.size, p.items]));
    rows.push([], ["Top Customers/Resellers"], ["No", "Customer", "Group", "TRX", "Items", "Total Sales"]);
    (data.top_customers || []).forEach((c, i) => rows.push([i + 1, c.name, c.group, c.trx, c.items, c.total]));
    const csv = rows.map((r) => r.map((x) => `"${String(x ?? "")}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `dashboard_${start}_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const renderWidget = (w) => {
    const span = SIZE_SPAN[w.size] || SIZE_SPAN.M;
    if (KPI_META[w.key]) return <div key={w.key} className={span}><KpiCard wkey={w.key} cards={cards} mask={masks[w.key]} percent={percents[w.key]} /></div>;
    if (w.key === "sales_trend") return <div key={w.key} className={span}><TrendChart title={w.label} data={data?.sales_trend} channels={channels} money testId="sales-trend" /></div>;
    if (w.key === "item_trend") return <div key={w.key} className={span}><TrendChart title={w.label} data={data?.item_trend} channels={channels} testId="item-trend" /></div>;
    if (w.key === "detail_cash") return <div key={w.key} className={span}><DetailCash rows={data?.detail_cash || []} /></div>;
    if (w.key === "recent_activity") return <div key={w.key} className={span}><RecentActivity rows={data?.recent_activity || []} /></div>;
    if (w.key === "top_products") return <div key={w.key} className={span}><TopProducts rows={data?.top_products || []} /></div>;
    if (w.key === "top_customers") return <div key={w.key} className={span}><TopCustomers rows={data?.top_customers || []} /></div>;
    return null;
  };

  return (
    <div data-testid="dashboard-page">
      <Panel className="p-6 mb-6 bg-gradient-to-r from-cocoa/5 to-transparent border-cocoa/10">
        <div className="text-sm text-wmuted flex items-center gap-2">
          <span>📅</span>{new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-cocoa mt-1" data-testid="dashboard-greeting">
          Halo, {user?.name?.split(" ")[0] || "User"}! <span className="inline-block">👋</span>
        </h1>
        <p className="text-lg text-cocoa/70 font-heading mt-0.5">{greeting()}</p>
      </Panel>

      <PendingShipmentsCard />

      <Panel className="p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs text-wmuted">Dari Tanggal</Label>
            <Input type="date" value={start} onChange={(e) => onDate(setStart)(e.target.value)} className="w-44 bg-white" data-testid="dash-start" /></div>
          <div><Label className="text-xs text-wmuted">Sampai Tanggal</Label>
            <Input type="date" value={end} onChange={(e) => onDate(setEnd)(e.target.value)} className="w-44 bg-white" data-testid="dash-end" /></div>
          <div className="flex items-center gap-1.5">
            {[["today", "Hari Ini"], ["yesterday", "Kemarin"], ["week", "Minggu Ini"], ["month", "Bulan Ini"]].map(([p, l]) => (
              <Button key={p} size="sm" variant={preset === p ? "default" : "outline"}
                onClick={() => applyPreset(p)} className={preset === p ? "bg-cocoa text-ivory" : "border-taupe text-cocoa"} data-testid={`preset-${p}`}>{l}</Button>
            ))}
          </div>
          <div><Label className="text-xs text-wmuted">Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="w-40 bg-white" data-testid="dash-channel"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Channel</SelectItem>
                {(data?.channels || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs text-wmuted">Group</Label>
            <Select value={group} onValueChange={setGroup}>
              <SelectTrigger className="w-40 bg-white" data-testid="dash-group"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Group</SelectItem>
                {(data?.group_options || []).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="ml-auto flex items-center gap-2">
            {can() && <Button size="sm" variant="outline" onClick={() => setCfgOpen(true)} className="border-taupe text-cocoa" data-testid="dash-settings-btn"><Settings2 className="w-4 h-4 mr-1.5" />Pengaturan</Button>}
            <Button size="sm" onClick={exportCsv} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dash-export-btn"><Download className="w-4 h-4 mr-1.5" />Export</Button>
          </div>
        </div>
      </Panel>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4" data-testid="dashboard-grid">
        <div className="col-span-full"><PrayerWidget /></div>
        {layout.map(renderWidget)}
        {!layout.length && <div className="col-span-full text-center text-wmuted py-12">Belum ada widget yang ditetapkan untuk role Anda.</div>}
      </div>

      {cfgOpen && <SettingsDialog onClose={() => { setCfgOpen(false); load(); }} />}
      {loading && <div className="fixed bottom-4 right-4 text-xs text-wmuted bg-white px-3 py-1.5 rounded shadow">Memuat...</div>}
    </div>
  );
}

const Empty = () => <div className="text-sm text-wmuted py-6 text-center">Belum ada data.</div>;

function KpiCard({ wkey, cards, mask, percent }) {
  const c = KPI_META[wkey];
  let display;
  if (mask === "masked") display = <span className="inline-flex items-center gap-1"><EyeOff className="w-4 h-4 text-wmuted" />••••</span>;
  else if (mask === "percent") display = `${percent ?? 0}%`;
  else display = c.money ? fmtIDR(cards[wkey]) : (cards[wkey] ?? 0);
  return (
    <Panel className="p-4 hover:-translate-y-0.5 transition-transform h-full">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-wmuted uppercase tracking-wide">{c.label}</span>
        <c.icon className={`w-4 h-4 ${c.color}`} />
      </div>
      <div className="mt-2 text-2xl font-heading font-bold text-cocoa truncate" data-testid={`card-${wkey}`}>{display}</div>
      <div className="text-xs text-wmuted mt-1">{c.sub && !mask ? `${cards[c.sub] ?? 0} ` : ""}{c.subLabel}</div>
    </Panel>
  );
}

function DetailCash({ rows }) {
  return (
    <Panel className="p-5 h-full" data-testid="detail-cash">
      <h3 className="font-heading font-bold text-cocoa mb-4">Detail Cash / Pembayaran</h3>
      <div className="space-y-1">
        {rows.map((d) => (
          <div key={d.method} className="flex items-center justify-between py-2 border-b border-taupe/10 last:border-0">
            <span className="text-sm text-cocoa">{d.method}</span>
            <span className="text-sm font-semibold text-cocoa">{fmtIDR(d.amount)}</span>
          </div>
        ))}
        {!rows.length && <Empty />}
      </div>
    </Panel>
  );
}

function RecentActivity({ rows }) {
  return (
    <Panel className="p-5 h-full" data-testid="recent-activity">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-cocoa">Aktivitas Pengguna Terbaru</h3>
        <Link to="/user-activity" className="text-xs font-medium text-cocoa hover:underline" data-testid="activity-more-link">Lihat semua →</Link>
      </div>
      <div className="space-y-2">
        {rows.slice(0, 7).map((a) => (
          <div key={a.id} className="flex items-start gap-3 py-2 border-b border-taupe/10 last:border-0">
            <div className="w-8 h-8 rounded-full bg-cocoa/10 text-cocoa flex items-center justify-center text-xs font-bold shrink-0">
              {(a.user_name || "?").slice(0, 2)}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-cocoa">{a.user_name}</div>
              <div className="text-sm text-cocoa/80">{describeActivity(a)}</div>
              <div className="text-[11px] text-wmuted mt-0.5">{timeAgo(a.datetime)}</div>
            </div>
          </div>
        ))}
        {!rows.length && <Empty />}
      </div>
    </Panel>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s} detik lalu`;
  if (s < 3600) return `${Math.floor(s / 60)} menit lalu`;
  if (s < 86400) return `${Math.floor(s / 3600)} jam lalu`;
  return `${Math.floor(s / 86400)} hari lalu`;
}

function TrendChart({ title, data, channels, money, testId }) {
  const fmt = (v) => money ? "Rp " + (v / 1000).toLocaleString("id-ID") + "k" : v;
  const label = (d) => { try { return new Date(d).toLocaleDateString("id-ID", { day: "numeric", month: "short" }); } catch { return d; } };
  return (
    <Panel className="p-5 h-full" data-testid={testId}>
      <h3 className="font-heading font-bold text-cocoa mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={data || []} margin={{ left: 8, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis dataKey="date" tickFormatter={label} tick={{ fill: "#8A7970", fontSize: 11 }} />
          <YAxis tickFormatter={fmt} tick={{ fill: "#8A7970", fontSize: 11 }} width={60} />
          <Tooltip formatter={(v) => money ? fmtIDR(v) : v} labelFormatter={label} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="Total" stroke="#1F2937" strokeWidth={2.5} dot={false} />
          {channels.map((c, i) => (
            <Line key={c} type="monotone" dataKey={c} stroke={CH_COLORS[i % CH_COLORS.length]} strokeWidth={1.8} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Panel>
  );
}

function usePaged(rows, size = 10) {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [rows]);
  const pages = Math.max(1, Math.ceil(rows.length / size));
  const slice = rows.slice((page - 1) * size, page * size);
  return { slice, page, pages, setPage, total: rows.length };
}

function Pager({ p }) {
  return (
    <div className="flex items-center justify-between mt-3 text-sm text-wmuted px-1">
      <span>Menampilkan {p.total ? (p.page - 1) * 10 + 1 : 0}–{Math.min(p.page * 10, p.total)} dari {p.total}</span>
      <div className="flex gap-1">
        <Button size="sm" variant="outline" disabled={p.page <= 1} onClick={() => p.setPage(p.page - 1)}>Prev</Button>
        <span className="px-2 py-1 text-cocoa font-semibold">{p.page}/{p.pages}</span>
        <Button size="sm" variant="outline" disabled={p.page >= p.pages} onClick={() => p.setPage(p.page + 1)}>Next</Button>
      </div>
    </div>
  );
}

function TopProducts({ rows }) {
  const p = usePaged(rows);
  return (
    <Panel className="p-5 h-full" data-testid="top-products">
      <h3 className="font-heading font-bold text-cocoa mb-4">Top Products</h3>
      <Table>
        <TableHeader><TableRow className="hover:bg-transparent">
          <TableHead className="w-10">No</TableHead><TableHead>Nama</TableHead><TableHead>Warna</TableHead><TableHead>Ukuran</TableHead><TableHead className="text-right">Items</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {p.slice.map((r, i) => (
            <TableRow key={i}><TableCell className="text-wmuted">{(p.page - 1) * 10 + i + 1}</TableCell>
              <TableCell className="font-medium text-cocoa">{r.name}</TableCell><TableCell>{r.color}</TableCell><TableCell>{r.size}</TableCell>
              <TableCell className="text-right font-bold text-cocoa">{r.items}</TableCell></TableRow>
          ))}
          {!p.total && <TableRow><TableCell colSpan={5} className="text-center text-wmuted py-8">Belum ada data.</TableCell></TableRow>}
        </TableBody>
      </Table>
      {p.total > 10 && <Pager p={p} />}
    </Panel>
  );
}

function TopCustomers({ rows }) {
  const p = usePaged(rows);
  const totals = rows.reduce((a, r) => ({ trx: a.trx + r.trx, items: a.items + r.items, total: a.total + r.total }), { trx: 0, items: 0, total: 0 });
  return (
    <Panel className="p-5 h-full" data-testid="top-customers">
      <h3 className="font-heading font-bold text-cocoa mb-4">Top Customers / Resellers</h3>
      <Table>
        <TableHeader><TableRow className="hover:bg-transparent">
          <TableHead className="w-10">No</TableHead><TableHead>Customer</TableHead><TableHead>Group</TableHead>
          <TableHead className="text-right">TRX</TableHead><TableHead className="text-right">Items</TableHead><TableHead className="text-right">Total</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {p.slice.map((r, i) => (
            <TableRow key={i}><TableCell className="text-wmuted">{(p.page - 1) * 10 + i + 1}</TableCell>
              <TableCell className="font-medium text-cocoa">{r.name}</TableCell>
              <TableCell><span className="text-xs px-2 py-0.5 rounded-full bg-taupe/15 text-cocoa">{r.group || "-"}</span></TableCell>
              <TableCell className="text-right">{r.trx}</TableCell><TableCell className="text-right">{r.items}</TableCell>
              <TableCell className="text-right font-semibold text-cocoa">{fmtIDR(r.total)}</TableCell></TableRow>
          ))}
          {!p.total && <TableRow><TableCell colSpan={6} className="text-center text-wmuted py-8">Belum ada data.</TableCell></TableRow>}
          {p.total > 0 && <TableRow className="bg-taupe/5 font-bold"><TableCell colSpan={3} className="text-right">Total</TableCell>
            <TableCell className="text-right">{totals.trx}</TableCell><TableCell className="text-right">{totals.items}</TableCell>
            <TableCell className="text-right text-cocoa">{fmtIDR(totals.total)}</TableCell></TableRow>}
        </TableBody>
      </Table>
      {p.total > 10 && <Pager p={p} />}
    </Panel>
  );
}

function SettingsDialog({ onClose }) {
  const [channels, setChannels] = useState([]);
  const [payments, setPayments] = useState([]);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/dashboard/settings").then((r) => {
      setChannels(r.data.channels || []); setPayments(r.data.payment_methods || []); setGroups(r.data.groups || []);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/dashboard/settings", { channels, payment_methods: payments.filter(Boolean) });
      toast.success("Pengaturan disimpan"); onClose();
    } catch { toast.error("Gagal menyimpan"); } finally { setSaving(false); }
  };

  const setCh = (i, patch) => setChannels((cs) => cs.map((c, idx) => idx === i ? { ...c, ...patch } : c));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dash-settings-dialog">
        <DialogHeader><DialogTitle>Pengaturan Dashboard</DialogTitle>
          <DialogDescription>Atur pemetaan Channel/Segment dan metode pembayaran untuk Detail Cash.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="font-semibold text-cocoa">Channel / Segment</Label>
              <Button size="sm" variant="outline" onClick={() => setChannels([...channels, { label: "", keywords: [], group_ids: [] }])} data-testid="add-channel-btn"><Plus className="w-3.5 h-3.5 mr-1" />Tambah</Button>
            </div>
            <p className="text-xs text-wmuted mb-2">Sale dipetakan ke channel bila group_id cocok, atau kata kunci muncul di nama/grup customer (urutan atas menang).</p>
            <div className="space-y-2">
              {channels.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center border border-taupe/20 rounded p-2">
                  <Input className="col-span-3 bg-white" placeholder="Label" value={c.label} onChange={(e) => setCh(i, { label: e.target.value })} data-testid={`ch-label-${i}`} />
                  <Input className="col-span-5 bg-white" placeholder="kata kunci, pisah koma" value={(c.keywords || []).join(", ")} onChange={(e) => setCh(i, { keywords: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} data-testid={`ch-kw-${i}`} />
                  <Select value={(c.group_ids || [])[0] || "none"} onValueChange={(v) => setCh(i, { group_ids: v === "none" ? [] : [v] })}>
                    <SelectTrigger className="col-span-3 bg-white"><SelectValue placeholder="Group" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">— tanpa group —</SelectItem>
                      {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" className="col-span-1 text-status-reject" onClick={() => setChannels(channels.filter((_, idx) => idx !== i))} data-testid={`ch-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="font-semibold text-cocoa">Metode Pembayaran (Detail Cash)</Label>
            <p className="text-xs text-wmuted mb-2">Pisahkan dengan koma. Pembayaran di luar daftar masuk ke "Other".</p>
            <Input className="bg-white" value={payments.join(", ")} onChange={(e) => setPayments(e.target.value.split(",").map((x) => x.trim()))} data-testid="payment-methods-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} className="bg-cocoa text-ivory" data-testid="save-dash-settings">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
